import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Avatar,
  Alert,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  NoteAdd as NoteAddIcon,
  Pending as PendingIcon,
  People as PeopleIcon,
  Link as LinkIcon,
  Create as CreateIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  AttachMoney as AttachMoneyIcon,
  Timer as TimerIcon,
  Computer as ComputerIcon,
  School as SchoolIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import StudentGetUser from 'utilities/api/StudentAllUserAPIs';
import ImageBaseURL from 'utilities/api/axios';
import { toast } from 'react-toastify';

const progress = {
  1: 'Not Started',
  2: 'In Progress',
  3: 'Completed',
};

const type = {
  1: 'Academic',
  2: 'Research',
  3: 'Personal',
  4: 'Other',
};

const platform = {
  1: 'Competitions',
  2: 'Exhibitions',
  3: 'Industry',
};

const ProjectDialog = ({
  open,
  onClose,
  handleSubmit,
  handleDelete,
  currentProject,
  isMobile
}) => {
  const { user } = useSelector((state) => state.user);
  const [teammateOptions, setTeammateOptions] = useState([]);
  const [currentTeammates, setCurrentTeammates] = useState([]);
  const [mentorOptions, setMentorOptions] = useState([]);
  const [currentMentors, setCurrentMentors] = useState([]);
  const [creator, setCreator] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [mentorSearchValue, setMentorSearchValue] = useState('');
  const [teammateError, setTeammateError] = useState('');
  const [mentorError, setMentorError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const prevProjectRef = React.useRef(null); // Track previous project to know when to reset form

  const theme = useTheme();

  const formik = useFormik({
    initialValues: {
      type: currentProject ? currentProject.type : '',
      progress_status: currentProject ? currentProject.progress_status : '',
      links: currentProject ? currentProject.links : '',
      title: currentProject ? currentProject.title : '',
      description: currentProject ? currentProject.description : '',
      student: user.id,
      platform: currentProject ? currentProject.platform : '',
      expenditure: currentProject ? currentProject.expenditure : '',
      budget: currentProject ? currentProject.budget : '',
      duration: currentProject ? currentProject.duration : '',
      is_patent_filed: currentProject?.is_patent_filed ?? false,
      is_project_visible: currentProject?.is_project_visible ?? true,
      mentors: currentProject ? currentProject.mentors : '',
    },
    onSubmit: async (values, { setErrors }) => {
      try {
        const teammates = currentTeammates.map((teammate) => teammate.id);

        const mentorEmailsString = currentMentors
          .map((mentor) => mentor.email || mentor.username)
          .join(", ");

        // Sanitize numeric values to avoid "valid integer required" error for empty strings
        const submissionValues = {
          ...values,
          teammates: teammates,
          mentors: mentorEmailsString,
          budget: values.budget === '' ? 0 : Number(values.budget),
          expenditure: values.expenditure === '' ? 0 : Number(values.expenditure),
          duration: values.duration === '' ? 0 : Number(values.duration),
        };
        await handleSubmit(submissionValues);
        // Explicitly reset states on success to handle reopen for new projects
        if (!currentProject) {
          formik.resetForm();
          setCurrentTeammates([]);
          setCurrentMentors([]);
          setSelectedFile(null);
        }
      } catch (error) {
        if (error.response?.status === 400 && error.response.data) {
          setErrors(error.response.data);
          toast.error('Please fix the errors in the form.');
        } else {
          // Toast is handled in projectscont if it's not a 400
          console.error('Error submitting project:', error);
        }
      }
    },
    enableReinitialize: false, // We've switched to manual setValues for better persistence control
  });

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0].name);
    }
  };

  const handleDocumentUrlChange = (event) => {
    setSelectedFile(event.target.value);
  };

  useEffect(() => {
    const fetchUserData = async (username) => {
      try {
        const userData = await StudentGetUser.UserDetailsGet(username);
        return userData[0];
      } catch (error) {
        console.error(`Error fetching data for ${username}:`, error);
        return null;
      }
    };

    const fetchProjectData = async () => {
      // If switching from one project to another, or from no project to a project, fill it.
      if (currentProject) {
        const creatorData = await fetchUserData(currentProject.student);
        setCreator(creatorData);

        const teammates = await Promise.all(
          currentProject.teammates_names.map(async (username) => {
            const teammateData = await fetchUserData(username);
            return teammateData;
          })
        );

        setCurrentTeammates(teammates.filter(Boolean));

        if (currentProject.mentors && typeof currentProject.mentors === 'string') {
          const mentorNames = currentProject.mentors.split(',').map(name => name.trim()).filter(Boolean);
          const mentors = await Promise.all(
            mentorNames.map(async (mentorName) => {
              try {
                const mentorData = await StudentGetUser.UserDetailsGet(mentorName);
                return mentorData[0];
              } catch (error) {
                return null;
              }
            })
          );
          setCurrentMentors(mentors.filter(Boolean));
        } else if (Array.isArray(currentProject.mentors)) {
          const mentors = await Promise.all(
            currentProject.mentors.map(async (mentor) => {
              const mentorUsername = typeof mentor === 'object' ? mentor.username : mentor;
              try {
                const mentorData = await StudentGetUser.UserDetailsGet(mentorUsername);
                return mentorData[0];
              } catch (error) {
                return null;
              }
            })
          );
          setCurrentMentors(mentors.filter(Boolean));
        }

        formik.setValues({
          type: currentProject.type || '',
          progress_status: currentProject.progress_status || '',
          links: currentProject.links || '',
          title: currentProject.title || '',
          description: currentProject.description || '',
          student: currentProject.student || user.id,
          platform: currentProject.platform || '',
          expenditure: currentProject.expenditure || '',
          budget: currentProject.budget || '',
          duration: currentProject.duration || '',
          is_patent_filed: currentProject.is_patent_filed ?? false,
          is_project_visible: currentProject.is_project_visible ?? true,
          mentors: '',
        });
      } else {
        // If we just finished an "Edit" session (prevProject was not null), we MUST reset.
        // If we are just closing/reopening an "Add" session (prevProject was null), we KEEP the draft.
        if (prevProjectRef.current !== null) {
          handleResetForm();
        }
      }
      prevProjectRef.current = currentProject;
    };

    fetchProjectData();
  }, [currentProject, user.id]);

  const handleResetForm = () => {
    formik.resetForm();
    setCurrentTeammates([]);
    setCurrentMentors([]);
    setCreator(null);
    setSelectedFile(null);
    setTeammateError('');
    setMentorError('');
  };

  const handleTeammateSearch = async (event, value) => {
    setSearchValue(value);

    try {
      const results = await StudentGetUser.UserDetailsGet(value || '');
      // Removed redundant TL_name filtering as the backend already filters by TL
      setTeammateOptions(results);
    } catch (error) {
      console.error('Error fetching teammate options:', error);
    }
  };

  const handleMentorSearch = async (event, value) => {
    setMentorSearchValue(value);

    try {
      const results = await StudentGetUser.UserDetailsGet(value || '');
      // Filter to only users that have email addresses
      const usersWithEmail = results.filter(user => user.email);
      setMentorOptions(usersWithEmail);
    } catch (error) {
      console.error('Error fetching mentor options:', error);
    }
  };

  const handleTeammateSelect = (event, value) => {
    // Filter out the creator (current user) from being added as a teammate
    const filteredValue = value.filter(
      (t) => String(t.id) !== String(user.id) && t.username !== user.username
    );

    if (filteredValue.length !== value.length) {
      setTeammateError('You cannot add yourself as a teammate.');
    } else {
      setTeammateError('');
    }

    setCurrentTeammates(filteredValue);
    setTeammateOptions([]);
    setSearchValue('');
  };

  const handleMentorSelect = (event, value) => {
    setCurrentMentors(value);
    setMentorError('');
    setMentorOptions([]);
    setMentorSearchValue('');
  };

  const handleRemoveTeammate = (teammateToRemove) => {
    setCurrentTeammates(currentTeammates.filter((teammate) => teammate.id !== teammateToRemove.id));
    setTeammateError('');
  };

  const handleRemoveMentor = (mentorToRemove) => {
    setCurrentMentors(currentMentors.filter((mentor) => mentor.id !== mentorToRemove.id));
    setMentorError('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      // fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: '#2c387e',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '16px' : '16px 24px',
          m: 0,
        }}
      >
        <Typography variant="h6">
          {currentProject ? 'Edit Project' : 'Add Project'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ p: 0, overflowY: 'auto' }}>
        <Box sx={{ padding: 2 }}>
          <form onSubmit={formik.handleSubmit}>
            <List>
              {/* All your existing ListItem fields remain exactly the same */}
              {/* Project Title */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CreateIcon />
                <TextField
                  variant="outlined"
                  sx={{ width: '100%' }}
                  placeholder="Add Project Title *"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="title"
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                />
              </ListItem>

              {/* Project Description */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                  height: '130px',
                }}
              >
                <DescriptionIcon sx={{ mb: '20px'}}/>
                <TextField
                  variant="outlined"
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '100px',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-inputMultiline': {
                      height: '100% !important',
                    }
                  }}
                  placeholder="Add Project Description *"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="description"
                  multiline
                  rows={4}
                  inputProps={{
                    maxLength: 250
                  }}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={(formik.touched.description && formik.errors.description) || `${formik.values.description.length}/250 characters`}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: 'right',
                      marginRight: 0
                    }
                  }}
                />
              </ListItem>

              {/* Project Type */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <NoteAddIcon />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel htmlFor="type-select">Type of Project *</InputLabel>
                  <Select
                    label="Type of Project"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    sx={{ width: '100%', borderRadius: '8px' }}
                    error={formik.touched.type && Boolean(formik.errors.type)}
                  >
                    {Object.entries(type).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.type && formik.errors.type && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                      {formik.errors.type}
                    </Typography>
                  )}
                </FormControl>
              </ListItem>

              {/* Project Progress Status */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <PendingIcon />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel htmlFor="progress-status-select">Project Progress Status *</InputLabel>
                  <Select
                    label="Project Progress Status"
                    name="progress_status"
                    value={formik.values.progress_status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    sx={{ width: '100%', borderRadius: '8px' }}
                    error={formik.touched.progress_status && Boolean(formik.errors.progress_status)}
                  >
                    {Object.entries(progress).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.progress_status && formik.errors.progress_status && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                      {formik.errors.progress_status}
                    </Typography>
                  )}
                </FormControl>
              </ListItem>

              {/* Project Platform */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <ComputerIcon />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel htmlFor="platform-select">Project Platform</InputLabel>
                  <Select
                    label="Project Platform"
                    name="platform"
                    value={formik.values.platform}
                    onChange={formik.handleChange}
                    sx={{ width: '100%', borderRadius: '8px' }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {Object.entries(platform).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>

              {/* Budget and Expenditure */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <AttachMoneyIcon />
                <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                  <TextField
                    variant="outlined"
                    sx={{ width: '50%' }}
                    label="Budget"
                    type="number"
                    value={formik.values.budget}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="budget"
                    error={formik.touched.budget && Boolean(formik.errors.budget)}
                    helperText={formik.touched.budget && formik.errors.budget}
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                  <TextField
                    variant="outlined"
                    sx={{ width: '50%' }}
                    label="Expenditure"
                    type="number"
                    value={formik.values.expenditure}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="expenditure"
                    error={formik.touched.expenditure && Boolean(formik.errors.expenditure)}
                    helperText={formik.touched.expenditure && formik.errors.expenditure}
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                </Box>
              </ListItem>

              {/* Duration */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <TimerIcon />
                <TextField
                  variant="outlined"
                  sx={{ width: '100%' }}
                  label="Duration (months)"
                  type="number"
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="duration"
                  error={formik.touched.duration && Boolean(formik.errors.duration)}
                  helperText={formik.touched.duration && formik.errors.duration}
                  InputProps={{
                    inputProps: { min: 0, step: 0.1 },
                  }}
                />
              </ListItem>

              {/* Patent & Visibility Switches */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.is_patent_filed}
                      onChange={(e) => formik.setFieldValue('is_patent_filed', e.target.checked)}
                      name="is_patent_filed"
                      color="warning"
                    />
                  }
                  label="Patent Filed"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.is_project_visible}
                      onChange={(e) => formik.setFieldValue('is_project_visible', e.target.checked)}
                      name="is_project_visible"
                      color="warning"
                    />
                  }
                  label="Project Visible"
                />
              </ListItem>

              {/* Mentors Section - Unchanged */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <SchoolIcon sx={{ mr: '4px' }} />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <Autocomplete
                    multiple
                    options={mentorOptions}
                    getOptionLabel={(option) =>
                      `${option.name || option.username} (${option.email || option.username})`
                    }
                    filterSelectedOptions
                    onInputChange={handleMentorSearch}
                    onFocus={() => handleMentorSearch(null, mentorSearchValue)}
                    onChange={handleMentorSelect}
                    value={currentMentors}
                    sx={{
                      width: '100%',
                      '& .MuiAutocomplete-inputRoot': { flexWrap: 'wrap', padding: '4px' },
                      '& .MuiInputBase-formControl': { height: 'auto'}
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Search and Add Mentors (email required)"
                      />
                    )}
                  />
                </FormControl>
              </ListItem>

              {mentorError && (
                <ListItem>
                  <Alert severity="error">{mentorError}</Alert>
                </ListItem>
              )}

              {currentMentors.length > 0 && (
                <ListItem sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, ml:'35px' }}>
                  <Typography variant="subtitle1">Current Mentors:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentMentors.map((mentor) => (
                      <Chip
                        key={mentor.id}
                        label={`${mentor.name || mentor.username} (${mentor.email || mentor.username})`}
                        avatar={
                          <Avatar
                            src={
                              mentor.profile_pic
                                ? `${ImageBaseURL.defaults.baseURL}${mentor.profile_pic}`
                                : ''
                            }
                          />
                        }
                        onDelete={() => handleRemoveMentor(mentor)}
                        deleteIcon={<DeleteIcon />}
                      />
                    ))}
                  </Box>
                </ListItem>
              )}

              {/* Documents Upload */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CloudUploadIcon />
                <Box sx={{ width: '100%' }}>
                  <TextField
                    variant="outlined"
                    sx={{ width: '100%' }}
                    placeholder="Enter document URL or upload file"
                    value={selectedFile || ''}
                    onChange={handleDocumentUrlChange}
                    name="documents"
                  />
                </Box>
              </ListItem>

              {/* Links */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <LinkIcon />
                <TextField
                  variant="outlined"
                  sx={{ width: '100%' }}
                  placeholder="Add Links (comma-separated URLs)"
                  value={formik.values.links}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="links"
                  error={formik.touched.links && Boolean(formik.errors.links)}
                  helperText={(formik.touched.links && formik.errors.links) || "Comma-separated URLs"}
                />
              </ListItem>

              {/* Creator */}
              {creator && (
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <PeopleIcon sx={{ mr: '4px' }} />
                  <Typography variant="body2" sx={{ marginRight: 2 }}>
                    Creator:
                  </Typography>
                  <Chip
                    avatar={
                      <Avatar
                        src={
                          creator?.profile_pic
                            ? `${ImageBaseURL.defaults.baseURL}${creator.profile_pic}`
                            : ''
                        }
                      />
                    }
                    label={`${creator?.name || creator?.username} (${creator?.username})`}
                  />
                </ListItem>
              )}

              <input type="hidden" name="student" value={formik.values.student} />

              {/* Teammates Section */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <PeopleIcon sx={{ mr: '4px' }} />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <Autocomplete
                    multiple
                    options={teammateOptions}
                    getOptionLabel={(option) => `${option.name || option.username} (${option.username})`}
                    filterSelectedOptions
                    onInputChange={handleTeammateSearch}
                    onFocus={() => handleTeammateSearch(null, searchValue)}
                    onChange={handleTeammateSelect}
                    value={currentTeammates}
                    sx={{
                      width: '100%',
                      '& .MuiAutocomplete-inputRoot': { flexWrap: 'wrap', padding: '4px' },
                      '& .MuiInputBase-formControl': { height: 'auto'}
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Search and Add Teammates"
                      />
                    )}
                  />
                </FormControl>
              </ListItem>

              {teammateError && (
                <ListItem>
                  <Alert severity="error">{teammateError}</Alert>
                </ListItem>
              )}

              {currentTeammates.length > 0 && (
                <ListItem sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, ml:'35px' }}>
                  <Typography variant="subtitle1">Current Teammates:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentTeammates.map((teammate) => (
                      <Chip
                        key={teammate.id}
                        label={`${teammate.name || teammate.username} (${teammate.username})`}
                        avatar={
                          <Avatar
                            src={
                              teammate.profile_pic
                                ? `${ImageBaseURL.defaults.baseURL}${teammate.profile_pic}`
                                : ''
                            }
                          />
                        }
                        onDelete={() => handleRemoveTeammate(teammate)}
                        deleteIcon={<DeleteIcon />}
                      />
                    ))}
                  </Box>
                </ListItem>
              )}
            </List>

            <Divider />
          </form>
        </Box>
      </DialogContent>

      {/* Actions (Footer) */}
      <DialogActions
        sx={{
          padding: isMobile ? '16px' : '16px 24px',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        {currentProject && (
          <Button
            variant="outlined"
            color="warning"
            onClick={handleDelete}
            sx={{
              minHeight: '48px',
              textTransform: 'none',
              borderRadius: '8px',
              border: '2px solid #212121',
              fontWeight: 600,
              color: '#C90505',
              width: 'auto',
            }}
          >
            Delete
          </Button>
        )}

        <Button
          type="submit"
          variant="contained"
          color="warning"
          onClick={formik.handleSubmit}
          sx={{
            minHeight: '48px',
            textTransform: 'none',
            backgroundColor: '#E79A2B',
            borderRadius: '8px',
            border: '2px solid #212121',
            fontWeight: 600,
            width: 'auto',
          }}
        >
          {currentProject ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDialog;